import React, { useState } from 'react';
import { Form, Input, Button, Card, message } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiService } from '../services/api';
import { LoginRequest } from '../types/auth';
import { clearAuthTokens } from '../utils/auth';

const isSafeRedirect = (value: string | null): value is string => {
  return !!value && value.startsWith('/') && !value.startsWith('//');
};

const Login: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const buildBasicAuthHeader = (username: string, password: string): string => {
    const encoded = btoa(unescape(encodeURIComponent(`${username}:${password}`)));
    return `Basic ${encoded}`;
  };

  const onFinish = async (values: LoginRequest) => {
    setLoading(true);
    try {
      const response = await apiService.login(values);
      
      // Проверяем наличие authorities
      if (!response.authorities || !Array.isArray(response.authorities) || response.authorities.length === 0) {
        // Если authorities не получены, разавторизовываем пользователя
        clearAuthTokens();
        message.error('Authorization failed: no authorities received');
        return;
      }
      
      localStorage.setItem('basicAuth', buildBasicAuthHeader(values.username, values.password));
      
      // Create user object from login response
      const user = {
        id: '', // Will be populated from token or separate API call if needed
        username: response.username,
        role: response.role,
        authorities: response.authorities
      };
      localStorage.setItem('currentUser', JSON.stringify(user));
      message.success('Login successful');

      const redirect = searchParams.get('redirect');
      if (isSafeRedirect(redirect)) {
        navigate(redirect);
      } else if (response.role === 'merchant') {
        navigate('/merchant/event');
      } else {
        navigate('/admin/dashboard');
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '100vh',
      backgroundColor: '#f0f2f5',
      padding: '16px'
    }}>
      <Card 
        title="Office Login" 
        style={{ 
          width: '100%', 
          maxWidth: 400,
          minWidth: 280
        }}
        headStyle={{ textAlign: 'center' }}
        className="mobile-padding"
      >
        <Form
          name="login"
          onFinish={onFinish}
          autoComplete="off"
          layout="vertical"
        >
          <Form.Item
            label="Username"
            name="username"
            rules={[{ required: true, message: 'Please input your username!' }]}
          >
            <Input 
              prefix={<UserOutlined />} 
              placeholder="Username" 
              size="large"
            />
          </Form.Item>

          <Form.Item
            label="Password"
            name="password"
            rules={[{ required: true, message: 'Please input your password!' }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Password"
              size="large"
            />
          </Form.Item>

          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              size="large"
              style={{ width: '100%' }}
            >
              Login
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default Login;
